/* Score summaries for a page of datasets, fetched in one request and joined in the client.
 *
 * **The join is here and not on the server, and that is an architectural decision rather than a
 * convenience.** Scores live in `core.scoring`; `.importlinter` contract 44 forbids `core.catalog`
 * reaching into it, and a cross-schema join in a catalog repository would put one module's storage
 * inside another's query — the coupling that makes a scoring migration break the catalog screen. The
 * comment on contract 51 already gives this answer for scoring and lineage: the screen calls both
 * endpoints and joins by id.
 *
 * One request for the whole page, not one per row. `/scores/latest` exists for exactly this.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { isScoreSummary } from '@/shared/api/generated/scoring';
import type { ScoreSummary } from '@/shared/api/generated/scoring';
import { getPage } from '@/shared/api/http';
import type { Page } from '@/shared/api/http';

export function latestScoresUrl(datasetIds: readonly string[]): string {
  const params = new URLSearchParams();
  for (const id of datasetIds) params.append('dataset_id', id);
  return `/v1/scoring/scores/latest?${params.toString()}`;
}

/** Scores keyed by dataset id. A dataset with no score is **absent**, never present as a null. */
export type ScoresByDataset = ReadonlyMap<string, ScoreSummary>;

export function useLatestScores(
  datasetIds: readonly string[],
): UseQueryResult<ScoresByDataset> {
  return useQuery({
    /* The ids are the key, sorted so the same page in a different order is one cache entry rather
     * than two. */
    queryKey: ['scoring', 'latest', [...datasetIds].sort()],
    queryFn: async () => {
      const page: Page<ScoreSummary> = await getPage(
        latestScoresUrl(datasetIds),
        isScoreSummary,
        'score summary',
      );
      return new Map(page.items.map((summary) => [summary.dataset_id, summary]));
    },
    /* Not fetched until there is a page to score. `enabled` rather than an early return, so the hook
     * is called unconditionally and React's rules of hooks hold. */
    enabled: datasetIds.length > 0,
    /* **The catalog must render without this.** A slow or broken scoring service leaves the score
     * column empty and the rest of the table usable — it does not error the screen. `retry: false`
     * because a failed batch read has no partial value to recover, and the catalog is already
     * on screen while this is in flight. */
    retry: false,
  });
}
